// Amplitude analytics wrapper.
//
// No-ops when NEXT_PUBLIC_AMPLITUDE_API_KEY is unset, so dev / preview builds
// without an API key don't blow up and don't pollute the production project.
// All call sites can fire-and-forget — `track` swallows errors.

"use client";

import { analyticsSdk, type EnrichmentPlugin } from "@/lib/analytics-sdk";
import { logger } from "@/lib/log";

const log = logger("analytics");

let initialized = false;
let activeWorkspaceKind: WorkspaceKind | null = null;

export type WorkspaceKind = "customer" | "internal" | "demo" | "dev";

function workspaceKind(value: unknown): WorkspaceKind | null {
  return value === "customer" || value === "internal" || value === "demo" || value === "dev"
    ? value
    : null;
}

// Strip secret tokens that ride in URLs before any event leaves the browser.
// Primary defense is disabling URL-bearing autocapture in initAnalytics; this
// is the backstop. Several PUBLIC routes carry a secret in the URL — a bearer
// token in the path (/share/<token>, /invite/<token>, /workspace-invite/<token>)
// or a Firebase action code in the query (/auth/action?oobCode=...) — and any
// event that still picks one up would leak it to Amplitude (a third party).
// Scrub every string property (recursively, to reach nested identify
// $set/$setOnce) on every outgoing event.
const SECRET_URL_PATTERNS: Array<[RegExp, string]> = [
  [/\/(share|invite|workspace-invite)\/[^/?#]+/gi, "/$1/[redacted]"],
  [/([?&](?:oobCode|token|apiKey)=)[^&#]+/gi, "$1[redacted]"],
];

function redactSecrets(value: string): string {
  return SECRET_URL_PATTERNS.reduce((s, [re, repl]) => s.replace(re, repl), value);
}

function scrubDeep(obj: unknown): void {
  if (!obj || typeof obj !== "object") return;
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    if (typeof v === "string") {
      (obj as Record<string, unknown>)[k] = redactSecrets(v);
    } else if (v && typeof v === "object") {
      scrubDeep(v);
    }
  }
}

const redactionPlugin: EnrichmentPlugin = {
  name: "redact-secret-urls",
  type: "enrichment",
  setup: async () => undefined,
  execute: async (event) => {
    scrubDeep(event.event_properties);
    scrubDeep(event.user_properties);
    return event;
  },
};

export function initAnalytics() {
  if (initialized) return;
  const apiKey = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY;
  if (!apiKey) return;
  try {
    analyticsSdk.init(apiKey, {
      // Pin local config as authoritative. By default the SDK fetches remote
      // config, whose autocapture settings would override the line below — so
      // someone toggling capture in the Amplitude UI could re-enable it.
      fetchRemoteConfig: false,
      // Disable ALL autocapture. Page views, attribution, sessions, and the
      // separate page-URL enrichment each attach the current URL / referrer to
      // events, and several PUBLIC routes carry a secret in the URL — a bearer
      // token in /share|/invite|/workspace-invite/<token>, or a Firebase action
      // code in /auth/action?oobCode=... — so any autocaptured event could leak
      // those to Amplitude (a third party). The dashboard is built entirely on
      // the explicit events below, so autocapture buys nothing here. (Re-enable
      // deliberately, with URL redaction, only if page/session analytics are
      // needed.)
      autocapture: false,
    });
    // Defense in depth: scrub any secret token that still reaches an event
    // (e.g. a future custom property carrying a URL). Registered AFTER init so
    // it runs after the SDK's own enrichers, not before them.
    analyticsSdk.add(redactionPlugin);
    initialized = true;
  } catch (err) {
    log.warn("amplitude init failed", err);
  }
}

// Force-send buffered events. Use before a navigation that unloads the page
// (e.g. an OAuth redirect) so a fire-and-forget track() isn't dropped.
export async function flushAnalytics(): Promise<void> {
  if (!initialized) return;
  try {
    await analyticsSdk.flush().promise;
  } catch (err) {
    log.warn("amplitude flush failed", err);
  }
}

export function identify(opts: {
  userId: string | null;
  email?: string | null;
  displayName?: string | null;
  tenantId?: string | null;
  tenantName?: string | null;
  workspaceKind?: WorkspaceKind | null;
  isStaff?: boolean;
}) {
  if (!opts.userId) activeWorkspaceKind = null;
  else activeWorkspaceKind = workspaceKind(opts.workspaceKind);
  if (!initialized) initAnalytics();
  if (!initialized) return;
  try {
    if (opts.userId) {
      analyticsSdk.setUserId(opts.userId);
    } else {
      analyticsSdk.reset();
      return;
    }
    const identifyEvent = analyticsSdk.newIdentify();
    if (opts.email) identifyEvent.set("email", opts.email);
    if (opts.displayName) identifyEvent.set("display_name", opts.displayName);
    if (opts.tenantId) identifyEvent.set("tenant_id", opts.tenantId);
    if (opts.tenantName) identifyEvent.set("tenant_name", opts.tenantName);
    if (activeWorkspaceKind) identifyEvent.set("workspace_kind", activeWorkspaceKind);
    else identifyEvent.unset("workspace_kind");
    if (typeof opts.isStaff === "boolean") identifyEvent.set("is_staff", opts.isStaff);
    identifyEvent.set("product", "ballcap-prototype");
    analyticsSdk.identify(identifyEvent);
    if (opts.tenantId) {
      analyticsSdk.setGroup("tenant_id", opts.tenantId);
      const groupEvent = analyticsSdk.newIdentify();
      if (activeWorkspaceKind) groupEvent.set("workspace_kind", activeWorkspaceKind);
      else groupEvent.unset("workspace_kind");
      analyticsSdk.groupIdentify("tenant_id", opts.tenantId, groupEvent);
    } else {
      analyticsSdk.setGroup("tenant_id", []);
    }
  } catch (err) {
    log.warn("amplitude identify failed", err);
  }
}

export function track(event: string, props?: Record<string, unknown>) {
  // Lazy-init so an event fired from a child mount effect before the Providers
  // init effect runs (React effects fire bottom-up) still lands instead of
  // being dropped — e.g. connector_oauth_callback on a deep-linked callback URL.
  if (!initialized) initAnalytics();
  if (!initialized) return;
  try {
    analyticsSdk.track(event, { ...(props ?? {}), workspace_kind: activeWorkspaceKind });
  } catch (err) {
    log.warn("amplitude track failed", { event, err });
  }
}
